import { useState } from 'react';
;
import { useSignalR } from '@/contexts/signalr-context';
import { useCopilot } from '@/providers/copilot-provider';
import { useAuth } from '@/contexts/auth-context';
import { FeedbackCategory } from '@/types/feedback';
import { submitFeedbackViaSignalR, createFeedbackSubmission } from '@/services/feedbackService.v2';

interface CopilotFeedbackScreenProps {
  onClose: () => void;
  itemName: string;
}

export function CopilotFeedbackScreen({ onClose, itemName }: CopilotFeedbackScreenProps) {
  const { setIsVisible } = useCopilot();
  const { send } = useSignalR();
  const { version: serverVersion = '', buildTime: serverBuildTime = '' } = useAuth() || {};
  const [rating, setRating] = useState<'positive' | 'neutral' | 'negative' | ''>('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    setError(null);
    
    try {
      // Create unified feedback submission
      const submission = createFeedbackSubmission({
        rating: rating as 'positive' | 'negative' | 'neutral',
        comments,
        itemName,
        category: FeedbackCategory.General,
        serverVersion,
        serverBuildTime
      });

      await submitFeedbackViaSignalR(send, submission);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };


  if (submitted) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ color: '#16a34a', marginBottom: 12 }}>Thank you for your feedback!</div>
        <button
          type="button"
          onClick={() => {
            onClose();
            setIsVisible(false);
          }}
          className="px-4 py-2 rounded bg-primary text-primary-foreground mt-2"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, minWidth: 340, maxWidth: 400 }}>
      <div className="text-lg font-semibold mb-1">Feedback for {itemName}</div>
      <div className="text-muted-foreground mb-5 text-sm">Please rate your experience and provide any additional comments.</div>
      {error && <div className="text-red-500 mb-2">{error}</div>}
      <div className="flex items-center justify-between gap-3 mb-4">
        <button
          type="button"
          aria-label="Positive"
          className={`flex-1 flex flex-col items-center p-3 rounded border ${rating === 'positive' ? 'border-primary bg-primary/5' : 'border-gray-300 bg-white hover:bg-slate-50'} transition focus:outline-none`}
          onClick={() => setRating('positive')}
          disabled={submitting}
        >
          <span style={{ fontSize: 24 }}>👍</span>
          <span className="mt-1 text-xs font-medium">Positive</span>
        </button>
        <button
          type="button"
          aria-label="Neutral"
          className={`flex-1 flex flex-col items-center p-3 rounded border ${rating === 'neutral' ? 'border-primary bg-primary/5' : 'border-gray-300 bg-white hover:bg-slate-50'} transition focus:outline-none`}
          onClick={() => setRating('neutral')}
          disabled={submitting}
        >
          <span style={{ fontSize: 24 }}>—</span>
          <span className="mt-1 text-xs font-medium">Neutral</span>
        </button>
        <button
          type="button"
          aria-label="Negative"
          className={`flex-1 flex flex-col items-center p-3 rounded border ${rating === 'negative' ? 'border-primary bg-primary/5' : 'border-gray-300 bg-white hover:bg-slate-50'} transition focus:outline-none`}
          onClick={() => setRating('negative')}
          disabled={submitting}
        >
          <span style={{ fontSize: 24 }}>👎</span>
          <span className="mt-1 text-xs font-medium">Negative</span>
        </button>
      </div>
      <textarea
        className="w-full rounded border border-gray-300 px-3 py-2 mb-5 text-sm resize-none focus:ring-2 focus:ring-primary"
        placeholder="Additional comments..."
        value={comments}
        onChange={e => setComments(e.target.value)}
        rows={3}
        disabled={submitting}
      />
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded border border-gray-300 bg-white text-gray-700"
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="px-4 py-2 rounded bg-primary text-primary-foreground disabled:opacity-50"
          onClick={handleSubmit}
          disabled={submitting || !rating}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </div>
  );
}
